import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/auth/authStore';
import { useChatDock, type ChatDockTab } from '@/stores/chatDockStore';
import { useClanStore } from '@/stores/clanStore';
import { clanApi } from '@/clan/clanApi';
import { getSocket } from '@/net/socket';
import { Avatar } from '@/art';
import {
  socialApi,
  type DmMessage,
  type FriendEntry,
  type GlobalMessage,
} from '@/social/socialApi';
import { UserQuickMenu } from './UserQuickMenu';
import { useInvites } from '@/stores/invitesStore';
import { useRoomStore } from '@/stores/roomStore';
import { useUIStore } from '@/stores/uiStore';
import { roomApi } from '@/rooms/roomApi';
import type { RoomInvitePayload } from '@shared/protocol';
import { useT } from '@/i18n/useI18n';
import { audio } from '@/audio/AudioService';

const DOCK_BOTTOM = 18;
const HOME_DOCK_BOTTOM = 122;

// Shared rooms embed their 6-char code in brackets, e.g. "…👉 [ABC123]".
// The code alphabet excludes 0/O/1/I, so the bracketed token is language- and
// translation-independent — we detect it on the receiver side to offer a Join.
const ROOM_CODE_RE = /\[([A-HJ-NP-Z2-9]{6})\]/;
function extractRoomCode(text: string): string | null {
  const m = ROOM_CODE_RE.exec(text);
  return m ? m[1]! : null;
}

interface ChatDockProps {
  /** Hide the dock entirely (login/loading/match-in-progress screens). */
  hidden?: boolean;
}

export function ChatDock({ hidden = false }: ChatDockProps) {
  const me = useAuthStore((s) => s.user);
  const open = useChatDock((s) => s.open);
  const setOpen = useChatDock((s) => s.setOpen);
  const toggle = useChatDock((s) => s.toggle);
  const tab = useChatDock((s) => s.tab);
  const setTab = useChatDock((s) => s.setTab);
  const dmWith = useChatDock((s) => s.dmWith);
  const openDm = useChatDock((s) => s.openDm);
  const closeDm = useChatDock((s) => s.closeDm);
  const unreadGlobal = useChatDock((s) => s.unreadGlobal);
  const unreadDmByUser = useChatDock((s) => s.unreadDmByUser);
  const pendingRequests = useChatDock((s) => s.pendingRequests);
  const inviteCount = useInvites((s) => s.invites.length);
  const bumpGlobal = useChatDock((s) => s.bumpGlobal);
  const bumpDm = useChatDock((s) => s.bumpDm);
  const clearGlobal = useChatDock((s) => s.clearGlobal);
  const clearDm = useChatDock((s) => s.clearDm);
  const screen = useUIStore((s) => s.screen);
  const t = useT();

  // World chat is high-volume background noise — it must NOT light up the red
  // badge on the floating bubble. Only direct/actionable events do: private
  // messages, friend requests and room invites.
  const totalUnread = pendingRequests + inviteCount +
    Object.values(unreadDmByUser).reduce((a, b) => a + b, 0);

  if (hidden || !me) return null;

  return (
    <>
      {/* Collapsed bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={t('chat.open_chat')}
          style={{
            position: 'fixed',
            right: 14,
            bottom: screen === 'home' ? HOME_DOCK_BOTTOM : DOCK_BOTTOM,
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--c-cyan), var(--c-purple))',
            border: '2px solid rgba(255,255,255,0.18)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.4), 0 0 18px rgba(34,211,238,0.45)',
            cursor: 'pointer',
            zIndex: 250,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChatBubbleGlyph />
          {totalUnread > 0 && (
            <span
              style={{
                position: 'absolute',
                top: -4,
                right: -4,
                minWidth: 20,
                height: 20,
                padding: '0 5px',
                borderRadius: 10,
                background: 'var(--c-red)',
                color: '#fff',
                fontSize: 11,
                fontFamily: 'var(--f-body)', fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid var(--bg-deep)',
              }}
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>
      )}

      {/* Expanded panel — drops up from the bubble's anchor */}
      {open && (
        <DockPanel
          tab={tab}
          setTab={setTab}
          dmWith={dmWith}
          openDm={openDm}
          closeDm={closeDm}
          unreadGlobal={unreadGlobal}
          unreadDmByUser={unreadDmByUser}
          bumpGlobal={bumpGlobal}
          bumpDm={bumpDm}
          clearGlobal={clearGlobal}
          clearDm={clearDm}
          onClose={() => { setOpen(false); closeDm(); }}
          onMinimize={() => toggle()}
        />
      )}
    </>
  );
}

// ── Panel ────────────────────────────────────────────────────────────

interface DockPanelProps {
  tab: ChatDockTab;
  setTab: (t: ChatDockTab) => void;
  dmWith: string | null;
  openDm: (uid: string) => void;
  closeDm: () => void;
  unreadGlobal: number;
  unreadDmByUser: Record<string, number>;
  bumpGlobal: () => void;
  bumpDm: (uid: string) => void;
  clearGlobal: () => void;
  clearDm: (uid: string) => void;
  onClose: () => void;
  onMinimize: () => void;
}

function DockPanel(props: DockPanelProps) {
  const t = useT();
  const {
    tab, setTab, dmWith, openDm, closeDm,
    unreadGlobal, unreadDmByUser, clearGlobal, clearDm,
    bumpGlobal, bumpDm, onClose, onMinimize,
  } = props;
  const me = useAuthStore((s) => s.user);

  // Tab body changes based on tab + dm selection.
  const dmCount = Object.values(unreadDmByUser).reduce((a, b) => a + b, 0);
  const pending = useChatDock((s) => s.pendingRequests);
  const inviteCount = useInvites((s) => s.invites.length);
  const unreadClan = useChatDock((s) => s.unreadClan);
  const clearClan = useChatDock((s) => s.clearClan);
  const inClan = useClanStore((s) => !!s.myClan);

  return (
    <div
      style={{
        position: 'fixed',
        right: 8,
        left: 8,
        bottom: DOCK_BOTTOM - 10,
        maxWidth: 380,
        marginLeft: 'auto',
        height: 'min(70vh, 480px)',
        background: 'rgba(15,18,32,0.97)',
        backdropFilter: 'blur(10px)',
        border: '1px solid var(--hairline)',
        borderRadius: 14,
        boxShadow: '0 12px 32px rgba(0,0,0,0.6), 0 0 24px rgba(34,211,238,0.15)',
        zIndex: 260,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 10px',
          borderBottom: '1px solid var(--hairline)',
          background: 'rgba(0,0,0,0.3)',
        }}
      >
        <TabBtn active={tab === 'global'} onClick={() => { setTab('global'); closeDm(); clearGlobal(); }} badge={unreadGlobal}>
          {t('chat.global')}
        </TabBtn>
        {inClan && (
          <TabBtn active={tab === 'clan'} onClick={() => { setTab('clan'); closeDm(); clearClan(); }} badge={unreadClan}>
            {t('chat.clan')}
          </TabBtn>
        )}
        <TabBtn active={tab === 'dms'} onClick={() => { setTab('dms'); }} badge={dmCount + pending + inviteCount}>
          {t('chat.dms')}
        </TabBtn>
        <div style={{ flex: 1 }} />
        <IconBtn onClick={onMinimize} title={t('chat.minimize')}><MinimizeGlyph /></IconBtn>
        <IconBtn onClick={onClose} title={t('chat.close')} variant="danger"><CloseGlyph /></IconBtn>
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {tab === 'global' && (
          <GlobalChatView meId={me?.id ?? ''} bumpGlobal={bumpGlobal} clearGlobal={clearGlobal} />
        )}
        {tab === 'clan' && (
          <ClanChatView meId={me?.id ?? ''} clearClan={clearClan} />
        )}
        {tab === 'dms' && !dmWith && (
          <DmListView openDm={(uid) => { openDm(uid); clearDm(uid); }} unreadByUser={unreadDmByUser} />
        )}
        {tab === 'dms' && dmWith && (
          <DmThreadView
            withUserId={dmWith}
            meId={me?.id ?? ''}
            onBack={closeDm}
            onIncoming={(uid) => bumpDm(uid)}
            clearUnread={() => clearDm(dmWith)}
          />
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, badge }: { active: boolean; onClick: () => void; children: React.ReactNode; badge?: number }) {
  return (
    <button
      onClick={onClick}
      style={{
        position: 'relative',
        background: active ? 'rgba(34,211,238,0.18)' : 'transparent',
        border: '1px solid',
        borderColor: active ? 'var(--c-cyan)' : 'var(--hairline)',
        color: active ? 'var(--c-cyan)' : 'var(--ink-muted)',
        fontFamily: 'var(--f-body)', fontWeight: 800,
        fontSize: 12,
        letterSpacing: '0.01em',
        padding: '6px 10px',
        borderRadius: 6,
        cursor: 'pointer',
      }}
    >
      {children}
      {badge != null && badge > 0 && (
        <span
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            borderRadius: 8,
            background: 'var(--c-red)',
            color: '#fff',
            fontSize: 9,
            fontFamily: 'var(--f-body)', fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

function IconBtn({ onClick, title, children, variant }: { onClick: () => void; title: string; children: React.ReactNode; variant?: 'danger' }) {
  const danger = variant === 'danger';
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      style={{
        width: 30,
        height: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${danger ? 'rgba(230,57,70,0.5)' : 'var(--hairline)'}`,
        background: danger ? 'rgba(230,57,70,0.12)' : 'rgba(255,255,255,0.05)',
        color: danger ? 'var(--c-red)' : 'var(--ink)',
        borderRadius: 7,
        cursor: 'pointer',
        lineHeight: 1,
      }}
    >
      {children}
    </button>
  );
}

// ── Chat glyphs (line-art, currentColor) ─────────────────────────────
function ChatBubbleGlyph() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.5 8.5 0 0 1-12.4 7.55L3 20.5l1.45-4.2A8.5 8.5 0 1 1 21 11.5z" />
      <circle cx="8.5" cy="11.7" r="0.6" fill="#fff" stroke="#fff" />
      <circle cx="12" cy="11.7" r="0.6" fill="#fff" stroke="#fff" />
      <circle cx="15.5" cy="11.7" r="0.6" fill="#fff" stroke="#fff" />
    </svg>
  );
}

function MinimizeGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function CloseGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

// ── Message bubble (tap-to-copy) ─────────────────────────────────────
// Mobile webviews don't reliably allow long-press text selection inside
// scroll containers, so a tap copies the whole message (room codes etc.).
function MessageBubble({ text, mine, accent }: { text: string; mine: boolean; accent: 'yellow' | 'cyan' }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const color = accent === 'yellow' ? 'var(--c-yellow)' : 'var(--c-cyan)';
  const tint = accent === 'yellow' ? 'rgba(244,199,36,0.18)' : 'rgba(34,211,238,0.18)';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* no-op */ }
      document.body.removeChild(ta);
    }
    audio.play('ui.tap');
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div
      onDoubleClick={copy}
      title={t('chat.copy_tip')}
      style={{
        position: 'relative',
        padding: '6px 10px',
        borderRadius: 10,
        background: mine ? tint : 'rgba(255,255,255,0.06)',
        border: '1px solid',
        borderColor: mine ? color : 'var(--hairline)',
        color: mine ? color : 'var(--ink)',
        fontSize: 12,
        wordBreak: 'break-word',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {text}
      {copied && (
        <span
          style={{
            position: 'absolute',
            top: -9,
            right: -4,
            fontSize: 9,
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: 6,
            background: 'var(--c-cyan)',
            color: '#04121a',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
            pointerEvents: 'none',
          }}
        >
          ✓ {t('chat.copied')}
        </span>
      )}
    </div>
  );
}

// Persistent one-liner so players discover the double-tap-to-copy gesture
// (handy for room codes shared in chat).
function CopyHint() {
  const t = useT();
  return (
    <div
      style={{
        alignSelf: 'center',
        margin: '2px 0 4px',
        padding: '3px 10px',
        borderRadius: 999,
        background: 'rgba(34,211,238,0.10)',
        border: '1px solid var(--hairline)',
        color: 'var(--ink-dim)',
        fontSize: 10,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {t('chat.copy_hint')}
    </div>
  );
}

// ── Global chat view ────────────────────────────────────────────────

function GlobalChatView({ meId, bumpGlobal, clearGlobal }: { meId: string; bumpGlobal: () => void; clearGlobal: () => void }) {
  const t = useT();
  const [msgs, setMsgs] = useState<GlobalMessage[]>([]);
  const [blockedIds, setBlockedIds] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [peekUserId, setPeekUserId] = useState<string | null>(null);
  const [joining, setJoining] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const openDmFromMenu = useChatDock((s) => s.openDm);
  const room = useRoomStore((s) => s.room);
  const setRoom = useRoomStore((s) => s.setRoom);
  const goScreen = useUIStore((s) => s.go);
  const setDockOpen = useChatDock((s) => s.setOpen);

  const joinByCode = async (code: string) => {
    if (joining) return;
    audio.play('ui.tap');
    // Already in this room (e.g. the sharer taps their own message) → just open it.
    if (room?.code === code) { setDockOpen(false); goScreen('room'); return; }
    setJoining(code);
    try {
      const r = await roomApi.join(code);
      setRoom(r.room);
      setDockOpen(false);
      goScreen('room');
    } catch (e: any) {
      void import('@/stores/toastStore').then(({ useToasts }) => {
        useToasts.getState().push({
          kind: 'warn',
          title: t('toast.could_not_join'),
          body: e?.message ?? t('toast.could_not_join_body'),
          ttlMs: 4000,
        });
      });
    } finally {
      setJoining(null);
    }
  };

  const refreshBlocked = () =>
    socialApi.blockedIds().then((d) => setBlockedIds(new Set(d.blockedIds))).catch(() => {});

  useEffect(() => {
    socialApi.globalHistory().then((d) => setMsgs(d.messages)).catch(() => {});
    void refreshBlocked();
    clearGlobal();
  }, []);

  useEffect(() => {
    const socket = getSocket();
    const onMsg = (m: GlobalMessage) => {
      setMsgs((prev) => [...prev, m].slice(-100));
      // Already viewing → no unread bump. But the listener is mounted globally
      // when dock is open; the parent already cleared on mount.
    };
    const onCleared = () => setMsgs([]);
    socket.on('chat:global', onMsg);
    socket.on('chat:cleared', onCleared);
    return () => {
      socket.off('chat:global', onMsg);
      socket.off('chat:cleared', onCleared);
    };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs.length]);

  const send = () => {
    const txt = text.trim();
    if (!txt || sending) return;
    setSending(true);
    audio.play('ui.message_send');
    const socket = getSocket();
    socket.emit('chat:global', { text: txt });
    setText('');
    setSending(false);
  };

  const shareRoom = () => {
    if (!room || sending) return;
    audio.play('ui.message_send');
    getSocket().emit('chat:global', { text: t('chat.share_room_msg', { code: room.code }) });
  };

  // mark bumpGlobal as used to silence unused-var lint if needed (suppressed at compile time)
  void bumpGlobal;

  return (
    <>
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <CopyHint />
        {msgs.length === 0 && (
          <div style={{ color: 'var(--ink-dim)', fontSize: 11, textAlign: 'center', padding: 24 }}>
            {t('chat.no_messages')}
          </div>
        )}
        {msgs.filter((m) => !blockedIds.has(m.sender.id)).map((m) => {
          const mine = m.sender.id === meId;
          const roomCode = extractRoomCode(m.text);
          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', gap: 6, alignItems: 'flex-end' }}>
              {!mine && (
                <div
                  onClick={() => setPeekUserId(m.sender.id)}
                  style={{ cursor: 'pointer' }}
                  title={t('chat.view_profile_tip')}
                >
                  <Avatar color={m.sender.avatarColor} size={22} photoUrl={m.sender.avatarUrl ?? undefined} characterUrl={m.sender.characterIdleUrl} frame={m.sender.frame} />
                </div>
              )}
              <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                {!mine && (
                  <div
                    onClick={() => setPeekUserId(m.sender.id)}
                    style={{
                      fontSize: 11,
                      color: 'var(--c-cyan)',
                      marginBottom: 2,
                      fontFamily: 'var(--f-body)', fontWeight: 700,
                      letterSpacing: '0.01em',
                      cursor: 'pointer',
                    }}
                  >
                    {m.sender.displayName}
                  </div>
                )}
                <MessageBubble text={m.text} mine={mine} accent="yellow" />
                {roomCode && (
                  <button
                    onClick={() => joinByCode(roomCode)}
                    disabled={joining === roomCode}
                    style={{
                      marginTop: 4,
                      padding: '6px 12px',
                      minHeight: 32,
                      borderRadius: 8,
                      background: 'rgba(155,93,229,0.18)',
                      border: '1px solid var(--c-purple)',
                      color: 'var(--c-purple)',
                      fontFamily: 'var(--f-display)',
                      fontSize: 11,
                      letterSpacing: '0.06em',
                      cursor: joining === roomCode ? 'wait' : 'pointer',
                      opacity: joining === roomCode ? 0.6 : 1,
                    }}
                  >
                    {room?.code === roomCode ? t('chat.open_room') : t('chat.join_room')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {room && (room.status === 'lobby' || room.status === 'starting') && (
        <button
          onClick={shareRoom}
          style={{
            margin: '0 10px 6px',
            padding: '8px 10px',
            minHeight: 36,
            borderRadius: 8,
            background: 'rgba(155,93,229,0.16)',
            border: '1px solid var(--c-purple)',
            color: 'var(--c-purple)',
            fontFamily: 'var(--f-display)',
            fontSize: 11,
            letterSpacing: '0.05em',
            cursor: 'pointer',
          }}
        >
          {t('chat.share_room_btn', { code: room.code })}
        </button>
      )}
      <Composer text={text} setText={setText} onSend={send} placeholder={t('chat.placeholder_global')} />
      {peekUserId && (
        <UserQuickMenu
          userId={peekUserId}
          onClose={() => { setPeekUserId(null); void refreshBlocked(); }}
          onOpenDm={(uid) => openDmFromMenu(uid)}
        />
      )}
    </>
  );
}

// ── Clan chat view ──────────────────────────────────────────────────

function ClanChatView({ meId, clearClan }: { meId: string; clearClan: () => void }) {
  const t = useT();
  const [msgs, setMsgs] = useState<GlobalMessage[]>([]);
  const [blockedIds, setBlockedIds] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [peekUserId, setPeekUserId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const openDmFromMenu = useChatDock((s) => s.openDm);

  const refreshBlocked = () =>
    socialApi.blockedIds().then((d) => setBlockedIds(new Set(d.blockedIds))).catch(() => {});

  useEffect(() => {
    clanApi.chatHistory().then((d) => setMsgs(d.messages)).catch(() => {});
    void refreshBlocked();
    clearClan();
  }, []);

  useEffect(() => {
    const socket = getSocket();
    const onMsg = (m: GlobalMessage) => {
      setMsgs((prev) => [...prev, m].slice(-100));
      clearClan(); // viewing the clan tab → keep it read
    };
    socket.on('chat:clan', onMsg);
    return () => { socket.off('chat:clan', onMsg); };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs.length]);

  const send = () => {
    const txt = text.trim();
    if (!txt || sending) return;
    setSending(true);
    audio.play('ui.message_send');
    getSocket().emit('chat:clan', { text: txt });
    setText('');
    setSending(false);
  };

  return (
    <>
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {msgs.length === 0 && (
          <div style={{ color: 'var(--ink-dim)', fontSize: 11, textAlign: 'center', padding: 24 }}>
            {t('chat.no_messages')}
          </div>
        )}
        {msgs.filter((m) => !blockedIds.has(m.sender.id)).map((m) => {
          const mine = m.sender.id === meId;
          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', gap: 6, alignItems: 'flex-end' }}>
              {!mine && (
                <div onClick={() => setPeekUserId(m.sender.id)} style={{ cursor: 'pointer' }} title={t('chat.view_profile_tip')}>
                  <Avatar color={m.sender.avatarColor} size={22} photoUrl={m.sender.avatarUrl ?? undefined} characterUrl={m.sender.characterIdleUrl} frame={m.sender.frame} />
                </div>
              )}
              <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                {!mine && (
                  <div onClick={() => setPeekUserId(m.sender.id)} style={{ fontSize: 11, color: 'var(--c-cyan)', marginBottom: 2, fontFamily: 'var(--f-body)', fontWeight: 700, cursor: 'pointer' }}>
                    {m.sender.displayName}
                  </div>
                )}
                <MessageBubble text={m.text} mine={mine} accent="cyan" />
              </div>
            </div>
          );
        })}
      </div>
      <Composer text={text} setText={setText} onSend={send} placeholder={t('chat.placeholder_clan')} />
      {peekUserId && (
        <UserQuickMenu
          userId={peekUserId}
          onClose={() => { setPeekUserId(null); void refreshBlocked(); }}
          onOpenDm={(uid) => openDmFromMenu(uid)}
        />
      )}
    </>
  );
}

// ── DM list view ────────────────────────────────────────────────────

interface DmListRow {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  avatarColor: 'red' | 'blue';
  online: boolean;
  preview: string | null;
  previewMine: boolean;
  lastAt: number;
  unread: number;
  isFriend: boolean;
  frame?: { border?: string | null; glow?: string | null } | null;
  characterIdleUrl?: string | null;
}

function DmListView({ openDm, unreadByUser }: { openDm: (uid: string) => void; unreadByUser: Record<string, number> }) {
  const t = useT();
  const [rows, setRows] = useState<DmListRow[]>([]);
  const [incoming, setIncoming] = useState<FriendEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);
  const [peekUserId, setPeekUserId] = useState<string | null>(null);
  const setPendingRequests = useChatDock((s) => s.setPendingRequests);
  // Pull live invites from the shared store so push events from socket appear
  // here immediately without a refetch.
  const invites = useInvites((s) => s.invites);
  const removeInvite = useInvites((s) => s.remove);
  const setRoom = useRoomStore((s) => s.setRoom);
  const goScreen = useUIStore((s) => s.go);

  const refresh = async () => {
    try {
      const [list, thr] = await Promise.all([socialApi.list(), socialApi.threads()]);
      setIncoming(list.incoming);
      // Mirror authoritative count back into the dock badge.
      setPendingRequests(list.incoming.length);

      // Build a unified row list: every friend + every DM peer (including
      // non-friend strangers who messaged me). Friend metadata wins for
      // displayName/avatar (most up-to-date), thread provides preview + unread.
      const byId = new Map<string, DmListRow>();

      list.friends.forEach((f) => {
        byId.set(f.userId, {
          userId: f.userId,
          displayName: f.displayName,
          avatarUrl: f.avatarUrl,
          avatarColor: f.avatarColor,
          online: f.online,
          preview: null,
          previewMine: false,
          lastAt: 0,
          unread: unreadByUser[f.userId] ?? 0,
          isFriend: true,
          frame: f.frame ?? null,
          characterIdleUrl: f.characterIdleUrl ?? null,
        });
      });

      thr.threads.forEach((t) => {
        const existing = byId.get(t.peer.id);
        if (existing) {
          existing.preview = t.lastMessage.text;
          existing.previewMine = t.lastMessage.mine;
          existing.lastAt = +new Date(t.lastMessage.createdAt);
          existing.unread = Math.max(existing.unread, t.unread);
          existing.online = t.peer.online;
          if (!existing.frame && t.peer.frame) existing.frame = t.peer.frame;
          if (!existing.characterIdleUrl && t.peer.characterIdleUrl) existing.characterIdleUrl = t.peer.characterIdleUrl;
        } else {
          byId.set(t.peer.id, {
            userId: t.peer.id,
            displayName: t.peer.displayName,
            avatarUrl: t.peer.avatarUrl,
            avatarColor: t.peer.avatarColor,
            online: t.peer.online,
            preview: t.lastMessage.text,
            previewMine: t.lastMessage.mine,
            lastAt: +new Date(t.lastMessage.createdAt),
            unread: t.unread,
            isFriend: false,
            frame: t.peer.frame ?? null,
            characterIdleUrl: t.peer.characterIdleUrl ?? null,
          });
        }
      });

      const merged = Array.from(byId.values()).sort((a, b) => {
        // Anyone with activity floats up, then friends, then alphabetical.
        if (b.lastAt !== a.lastAt) return b.lastAt - a.lastAt;
        if (a.isFriend !== b.isFriend) return a.isFriend ? -1 : 1;
        return a.displayName.localeCompare(b.displayName);
      });
      setRows(merged);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  useEffect(() => {
    const socket = getSocket();
    const onPresence = (p: { userId: string; online: boolean }) => {
      setRows((prev) => prev.map((r) => r.userId === p.userId ? { ...r, online: p.online } : r));
    };
    const onDm = (_m: DmMessage) => {
      void refresh();
    };
    const onFriend = () => { void refresh(); };
    socket.on('presence:update', onPresence);
    socket.on('chat:dm', onDm);
    socket.on('friend:request', onFriend);
    socket.on('friend:accepted', onFriend);
    return () => {
      socket.off('presence:update', onPresence);
      socket.off('chat:dm', onDm);
      socket.off('friend:request', onFriend);
      socket.off('friend:accepted', onFriend);
    };
  }, []);

  async function handleAccept(userId: string) {
    setActing(userId);
    try {
      await socialApi.accept(userId);
      await refresh();
    } catch { /* noop */ }
    setActing(null);
  }

  async function handleDecline(userId: string) {
    setActing(userId);
    try {
      await socialApi.remove(userId);
      await refresh();
    } catch { /* noop */ }
    setActing(null);
  }

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-dim)', fontSize: 11 }}>{t('common.loading')}</div>;
  }
  if (rows.length === 0 && incoming.length === 0 && invites.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-dim)', fontSize: 11 }}>
        {t('chat.no_dms')}
      </div>
    );
  }

  async function handleAcceptInvite(code: string) {
    setActing(code);
    try {
      const r = await roomApi.acceptInvite(code);
      removeInvite(code);
      setRoom(r.room);
      goScreen('room');
    } catch (e: any) {
      removeInvite(code);
      console.warn('[invite] accept failed', e?.message);
    }
    setActing(null);
  }
  async function handleDeclineInvite(code: string) {
    setActing(code);
    try { await roomApi.declineInvite(code); } catch { /* noop */ }
    removeInvite(code);
    setActing(null);
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <AddFriendInline onAdded={() => void refresh()} />
      {invites.length > 0 && (
        <>
          <SectionLabel color="var(--c-purple)">{t('chat.section.room_invites', { n: invites.length })}</SectionLabel>
          {invites.map((inv: RoomInvitePayload) => (
            <div
              key={`inv-${inv.code}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderBottom: '1px solid var(--hairline)',
                background: 'rgba(155, 93, 229, 0.08)',
              }}
            >
              <Avatar color={inv.from.avatarColor} size={32} photoUrl={inv.from.avatarUrl ?? undefined} characterUrl={inv.from.characterIdleUrl} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--f-body)', fontWeight: 700, fontSize: 12 }}>{inv.from.displayName}</div>
                <div style={{ fontSize: 9, color: 'var(--c-purple)' }}>
                  {t('chat.invite.tagline', { code: inv.code })}
                </div>
              </div>
              <button
                disabled={acting === inv.code}
                onClick={() => handleAcceptInvite(inv.code)}
                style={{
                  background: 'var(--c-purple)',
                  color: '#fff',
                  border: 'none',
                  fontFamily: 'var(--f-body)', fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: '0.06em',
                  padding: '6px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  minHeight: 30,
                }}
              >
                {t('chat.invite.join')}
              </button>
              <button
                disabled={acting === inv.code}
                onClick={() => handleDeclineInvite(inv.code)}
                style={{
                  background: 'rgba(230,57,70,0.12)',
                  color: 'var(--c-red)',
                  border: '1px solid rgba(230,57,70,0.4)',
                  borderRadius: 6,
                  padding: '6px 9px',
                  cursor: 'pointer',
                  fontSize: 11,
                  minHeight: 30,
                }}
                title={t('chat.invite.decline')}
              >
                ×
              </button>
            </div>
          ))}
        </>
      )}
      {incoming.length > 0 && (
        <>
          <SectionLabel color="var(--c-yellow)">{t('chat.section.friend_requests', { n: incoming.length })}</SectionLabel>
          {incoming.map((f) => (
            <div
              key={`inc-${f.userId}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderBottom: '1px solid var(--hairline)',
                background: 'rgba(244,199,36,0.05)',
              }}
            >
              <div onClick={() => setPeekUserId(f.userId)} style={{ position: 'relative', cursor: 'pointer' }}>
                <Avatar color={f.avatarColor} size={32} photoUrl={f.avatarUrl ?? undefined} characterUrl={f.characterIdleUrl} frame={f.frame} />
                <span style={onlineDotStyle(f.online)} />
              </div>
              <div onClick={() => setPeekUserId(f.userId)} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                <div style={{ fontFamily: 'var(--f-body)', fontWeight: 700, fontSize: 12 }}>{f.displayName}</div>
                <div style={{ fontSize: 9, color: 'var(--c-yellow)' }}>{t('chat.request.tagline')}</div>
              </div>
              <button
                disabled={acting === f.userId}
                onClick={() => handleAccept(f.userId)}
                style={{
                  background: 'var(--c-cyan)',
                  color: 'var(--bg-deep)',
                  border: 'none',
                  fontFamily: 'var(--f-body)', fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: '0.04em',
                  padding: '6px 9px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  minHeight: 30,
                }}
                title={t('chat.request.accept')}
              >
                ✓
              </button>
              <button
                disabled={acting === f.userId}
                onClick={() => handleDecline(f.userId)}
                style={{
                  background: 'rgba(230,57,70,0.12)',
                  color: 'var(--c-red)',
                  border: '1px solid rgba(230,57,70,0.4)',
                  fontFamily: 'var(--f-body)', fontWeight: 700,
                  fontSize: 11,
                  padding: '6px 9px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  minHeight: 30,
                }}
                title={t('chat.request.decline')}
              >
                ×
              </button>
              <button
                onClick={() => openDm(f.userId)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--hairline)',
                  color: 'var(--ink-muted)',
                  borderRadius: 6,
                  padding: '6px 8px',
                  cursor: 'pointer',
                  fontSize: 12,
                  minHeight: 30,
                }}
                title={t('chat.open_dm')}
              >
                💬
              </button>
            </div>
          ))}
          <SectionLabel color="var(--ink-dim)">{t('chat.section.conversations')}</SectionLabel>
        </>
      )}
      {rows.map((r) => (
        <div
          key={r.userId}
          onClick={() => openDm(r.userId)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 10px',
            cursor: 'pointer',
            borderBottom: '1px solid var(--hairline)',
            background: r.unread > 0 ? 'rgba(34,211,238,0.06)' : 'transparent',
          }}
        >
          <div
            onClick={(e) => { e.stopPropagation(); setPeekUserId(r.userId); }}
            style={{ position: 'relative', cursor: 'pointer' }}
            title={t('chat.manage_friend_tip')}
          >
            <Avatar color={r.avatarColor} size={32} photoUrl={r.avatarUrl ?? undefined} characterUrl={r.characterIdleUrl} frame={r.frame} />
            <span style={onlineDotStyle(r.online)} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontFamily: 'var(--f-body)', fontWeight: 700, fontSize: 12, letterSpacing: '0.04em' }}>
                {r.displayName}
              </span>
              {!r.isFriend && (
                <span style={{ fontSize: 8, color: 'var(--ink-dim)', border: '1px solid var(--hairline)', padding: '0 4px', borderRadius: 3 }}>
                  NEW
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 10,
                color: r.unread > 0 ? 'var(--ink)' : 'var(--ink-dim)',
                fontWeight: r.unread > 0 ? 600 : 400,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 220,
              }}
            >
              {r.preview ? (r.previewMine ? t('chat.you_prefix', { text: r.preview }) : r.preview) : (r.online ? t('common.online') : t('common.offline'))}
            </div>
          </div>
          {r.unread > 0 && (
            <span style={{
              minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
              background: 'var(--c-red)', color: '#fff', fontSize: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--f-body)', fontWeight: 700,
            }}>
              {r.unread > 99 ? '99+' : r.unread}
            </span>
          )}
        </div>
      ))}
      {peekUserId && (
        <UserQuickMenu
          userId={peekUserId}
          onClose={() => { setPeekUserId(null); void refresh(); }}
          onOpenDm={(uid) => { setPeekUserId(null); openDm(uid); }}
        />
      )}
    </div>
  );
}

function AddFriendInline({ onAdded }: { onAdded: () => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState<string | null>(null);

  async function submit() {
    const u = name.trim();
    if (!u) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await socialApi.requestByUsername(u);
      setOk(r.accepted ? t('chat.now_friends') : t('chat.request_sent'));
      setName('');
      onAdded();
      window.setTimeout(() => setOk(null), 1800);
    } catch (e: any) {
      setErr(e?.message ?? t('chat.send_failed'));
    }
    setBusy(false);
  }

  return (
    <div style={{ padding: '8px 10px 4px', borderBottom: '1px solid var(--hairline)' }}>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          style={{
            width: '100%',
            background: 'rgba(244,199,36,0.1)',
            border: '1px dashed var(--c-yellow)',
            color: 'var(--c-yellow)',
            fontFamily: 'var(--f-body)', fontWeight: 700,
            fontSize: 11,
            letterSpacing: '0.02em',
            padding: '8px 10px',
            borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          {t('chat.add_friend')}
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              placeholder={t('chat.username_placeholder')}
              maxLength={32}
              style={{
                flex: 1,
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid var(--hairline)',
                color: 'var(--ink)',
                fontSize: 12,
                padding: '8px 10px',
                borderRadius: 6,
                outline: 'none',
              }}
            />
            <button
              onClick={submit}
              disabled={busy || !name.trim()}
              style={{
                background: name.trim() ? 'var(--c-yellow)' : 'var(--ink-dim)',
                color: 'var(--bg-deep)',
                border: 'none',
                fontFamily: 'var(--f-body)', fontWeight: 700,
                fontSize: 10,
                padding: '0 10px',
                borderRadius: 6,
                cursor: name.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              {t('chat.btn_add')}
            </button>
            <button
              onClick={() => { setOpen(false); setName(''); setErr(null); setOk(null); }}
              style={{
                background: 'transparent',
                border: '1px solid var(--hairline)',
                color: 'var(--ink-muted)',
                borderRadius: 6,
                padding: '0 10px',
                cursor: 'pointer',
                fontSize: 11,
              }}
            >
              ×
            </button>
          </div>
          {err && <div style={{ fontSize: 10, color: 'var(--c-red)' }}>{err}</div>}
          {ok && <div style={{ fontSize: 10, color: 'var(--c-cyan)' }}>✓ {ok}</div>}
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <div
      style={{
        padding: '8px 12px 4px',
        fontFamily: 'var(--f-body)', fontWeight: 800,
        fontSize: 11,
        letterSpacing: '0.02em',
        color,
        textTransform: 'none',
      }}
    >
      {children}
    </div>
  );
}

function onlineDotStyle(online: boolean): React.CSSProperties {
  return {
    position: 'absolute',
    bottom: -1,
    right: -1,
    width: 10,
    height: 10,
    borderRadius: 5,
    background: online ? 'var(--c-cyan)' : 'var(--ink-dim)',
    border: '2px solid var(--bg-deep)',
    boxShadow: online ? '0 0 4px var(--c-cyan)' : 'none',
  };
}

// ── DM thread view ──────────────────────────────────────────────────

function DmThreadView({
  withUserId, meId, onBack, onIncoming, clearUnread,
}: {
  withUserId: string;
  meId: string;
  onBack: () => void;
  onIncoming: (uid: string) => void;
  clearUnread: () => void;
}) {
  const t = useT();
  const [msgs, setMsgs] = useState<DmMessage[]>([]);
  const [text, setText] = useState('');
  const [peer, setPeer] = useState<FriendEntry | null>(null);
  const [peekOpen, setPeekOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    socialApi.dmHistory(withUserId).then((d) => setMsgs(d.messages)).catch(() => {});
    socialApi.list().then((d) => {
      setPeer(d.friends.find((f) => f.userId === withUserId) ?? null);
    });
    clearUnread();
  }, [withUserId]);

  useEffect(() => {
    const socket = getSocket();
    const onMsg = (m: DmMessage) => {
      const involves = (m.fromId === withUserId && m.toId === meId) ||
                       (m.fromId === meId && m.toId === withUserId);
      if (!involves) {
        // Different conversation — bump that user's unread for the dock badge.
        if (m.toId === meId) onIncoming(m.fromId);
        return;
      }
      setMsgs((prev) => [...prev, m]);
    };
    socket.on('chat:dm', onMsg);
    return () => { socket.off('chat:dm', onMsg); };
  }, [withUserId, meId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs.length]);

  const send = () => {
    const txt = text.trim();
    if (!txt) return;
    audio.play('ui.message_send');
    getSocket().emit('chat:dm', { toId: withUserId, text: txt });
    setText('');
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderBottom: '1px solid var(--hairline)' }}>
        <button
          onClick={onBack}
          aria-label={t('chat.back_to_conversations')}
          style={{
            background: 'rgba(34,211,238,0.12)',
            border: '1px solid var(--c-cyan)',
            color: 'var(--c-cyan)',
            fontFamily: 'var(--f-body)', fontWeight: 700,
            fontSize: 11,
            letterSpacing: '0.06em',
            cursor: 'pointer',
            padding: '8px 12px',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            minHeight: 36,
          }}
        >
          ← {t('common.back')}
        </button>
        <div
          onClick={() => setPeekOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, cursor: 'pointer' }}
        >
          {peer && <Avatar color={peer.avatarColor} size={28} photoUrl={peer.avatarUrl ?? undefined} characterUrl={peer.characterIdleUrl} frame={peer.frame} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--f-body)', fontWeight: 700, fontSize: 12 }}>{peer?.displayName ?? '…'}</div>
            <div style={{ fontSize: 9, color: peer?.online ? 'var(--c-cyan)' : 'var(--ink-dim)' }}>
              {peer?.online ? `● ${t('common.online')}` : `○ ${t('common.offline')}`}
            </div>
          </div>
        </div>
      </div>
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <CopyHint />
        {msgs.map((m) => {
          const mine = m.fromId === meId;
          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '78%' }}>
                <MessageBubble text={m.text} mine={mine} accent="cyan" />
              </div>
            </div>
          );
        })}
      </div>
      <Composer text={text} setText={setText} onSend={send} placeholder={t('chat.placeholder_dm', { name: peer?.displayName ?? '' })} />
      {peekOpen && (
        <UserQuickMenu
          userId={withUserId}
          onClose={() => setPeekOpen(false)}
          onOpenDm={() => setPeekOpen(false)}
        />
      )}
    </>
  );
}

// ── Composer ────────────────────────────────────────────────────────

function Composer({ text, setText, onSend, placeholder }: { text: string; setText: (v: string) => void; onSend: () => void; placeholder: string }) {
  const t = useT();
  return (
    <div style={{ display: 'flex', gap: 6, padding: 8, borderTop: '1px solid var(--hairline)', background: 'rgba(0,0,0,0.3)' }}>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onSend()}
        placeholder={placeholder}
        maxLength={280}
        style={{
          flex: 1,
          background: 'rgba(0,0,0,0.4)',
          border: '1px solid var(--hairline)',
          color: 'var(--ink)',
          fontSize: 12,
          padding: '8px 10px',
          borderRadius: 6,
          outline: 'none',
        }}
      />
      <button
        onClick={onSend}
        disabled={!text.trim()}
        style={{
          background: text.trim() ? 'var(--c-cyan)' : 'var(--ink-dim)',
          color: 'var(--bg-deep)',
          border: 'none',
          fontFamily: 'var(--f-body)', fontWeight: 700,
          fontSize: 10,
          letterSpacing: '0.02em',
          padding: '0 12px',
          borderRadius: 6,
          cursor: text.trim() ? 'pointer' : 'not-allowed',
        }}
      >
        {t('common.send')}
      </button>
    </div>
  );
}

// ── Global notifier — counts unread when the dock is closed ─────────
// Mounted once at App-level. Keeps unread counters in sync with incoming
// socket events even if the dock panel itself isn't open, and surfaces a
// toast for incoming friend requests / accepts.
// Cross-type throttle so a burst of DMs / requests / invites (e.g. when the
// lobby is crowded) can't machine-gun notification blips. At most one notify
// sound per window; the badges still update for every event.
let lastNotifyAt = 0;
function playNotify(key: string): void {
  const now = Date.now();
  if (now - lastNotifyAt < 1500) return;
  lastNotifyAt = now;
  audio.play(key);
}

export function ChatNotifier() {
  const t = useT();
  const me = useAuthStore((s) => s.user);
  const open = useChatDock((s) => s.open);
  const tab = useChatDock((s) => s.tab);
  const dmWith = useChatDock((s) => s.dmWith);
  const bumpGlobal = useChatDock((s) => s.bumpGlobal);
  const bumpClan = useChatDock((s) => s.bumpClan);
  const bumpDm = useChatDock((s) => s.bumpDm);
  const bumpPending = useChatDock((s) => s.bumpPendingRequests);
  const setPending = useChatDock((s) => s.setPendingRequests);
  const setOpen = useChatDock((s) => s.setOpen);
  const setTab = useChatDock((s) => s.setTab);
  const openDmFn = useChatDock((s) => s.openDm);

  // Hydrate pending friend requests + room invites on login / reconnect.
  useEffect(() => {
    if (!me) return;
    void import('@/social/socialApi').then(({ socialApi }) => {
      socialApi.list().then((d) => setPending(d.incoming.length)).catch(() => {});
    });
    void Promise.all([import('@/rooms/roomApi'), import('@/stores/invitesStore')])
      .then(([apiM, storeM]) => {
        apiM.roomApi.myInvites().then((d) => {
          storeM.useInvites.getState().setAll(d.invites);
        }).catch(() => {});
      });
  }, [me, setPending]);

  useEffect(() => {
    if (!me) return;
    const socket = getSocket();
    const onGlobal = (_m: GlobalMessage) => {
      // Global chat is too noisy to toast — only bump the badge so the user
      // can see "there's activity" without being interrupted. DMs + friend
      // requests still toast because they're direct/actionable.
      if (open && tab === 'global') return;
      bumpGlobal();
    };
    const onClan = (_m: GlobalMessage) => {
      if (open && tab === 'clan') return;
      bumpClan();
    };
    const onDm = (m: DmMessage) => {
      if (m.toId !== me.id) return;
      if (open && tab === 'dms' && dmWith === m.fromId) return;
      bumpDm(m.fromId);
      playNotify('ui.notify_dm');
      void import('@/stores/toastStore').then(({ useToasts }) => {
        useToasts.getState().push({
          kind: 'success',
          title: t('toast.dm_new'),
          body: m.text,
          ttlMs: 4500,
          onClick: () => openDmFn(m.fromId),
        });
      });
    };
    const onFriendRequest = (payload: { from: { userId: string; displayName: string; avatarColor: 'red' | 'blue'; avatarUrl: string | null; characterIdleUrl?: string | null } }) => {
      bumpPending();
      playNotify('ui.notify_friend_request');
      void import('@/stores/toastStore').then(({ useToasts }) => {
        useToasts.getState().push({
          kind: 'friend',
          title: t('toast.friend_request'),
          body: t('toast.friend_request_body', { name: payload.from.displayName }),
          avatarColor: payload.from.avatarColor,
          avatarUrl: payload.from.avatarUrl,
          characterUrl: payload.from.characterIdleUrl,
          ttlMs: 7000,
          onClick: () => { setTab('dms'); setOpen(true); },
        });
      });
    };
    const onFriendAccepted = (payload: { friend: { displayName: string; avatarColor: 'red' | 'blue'; avatarUrl: string | null; characterIdleUrl?: string | null } }) => {
      void import('@/stores/toastStore').then(({ useToasts }) => {
        useToasts.getState().push({
          kind: 'success',
          title: t('toast.now_friends'),
          body: t('toast.now_friends_body', { name: payload.friend.displayName }),
          avatarColor: payload.friend.avatarColor,
          avatarUrl: payload.friend.avatarUrl,
          characterUrl: payload.friend.characterIdleUrl,
          ttlMs: 5000,
          onClick: () => { setTab('dms'); setOpen(true); },
        });
      });
    };
    const onRoomInvite = (payload: import('@shared/protocol').RoomInvitePayload) => {
      playNotify('ui.notify_room_invite');
      // Persist in invites store + toast. Tap toast → accept invite directly.
      void import('@/stores/invitesStore').then(({ useInvites }) => {
        useInvites.getState().add(payload);
      });
      void Promise.all([
        import('@/stores/toastStore'),
        import('@/rooms/roomApi'),
        import('@/stores/roomStore'),
        import('@/stores/uiStore'),
        import('@/stores/invitesStore'),
      ]).then(([toastM, apiM, roomM, uiM, invM]) => {
        toastM.useToasts.getState().push({
          kind: 'friend',
          title: t('toast.room_invite', { name: payload.from.displayName }),
          body: t('toast.room_invite_body', { code: payload.code }),
          avatarColor: payload.from.avatarColor,
          avatarUrl: payload.from.avatarUrl,
          characterUrl: payload.from.characterIdleUrl,
          ttlMs: 9000,
          onClick: async () => {
            try {
              const r = await apiM.roomApi.acceptInvite(payload.code);
              invM.useInvites.getState().remove(payload.code);
              roomM.useRoomStore.getState().setRoom(r.room);
              uiM.useUIStore.getState().go('room');
            } catch (e: any) {
              toastM.useToasts.getState().push({
                kind: 'warn',
                title: t('toast.could_not_join'),
                body: e?.message ?? t('toast.could_not_join_body'),
                ttlMs: 4000,
              });
            }
          },
        });
      });
    };
    socket.on('chat:global', onGlobal);
    socket.on('chat:clan', onClan);
    socket.on('chat:dm', onDm);
    socket.on('friend:request', onFriendRequest);
    socket.on('friend:accepted', onFriendAccepted);
    socket.on('room:invite', onRoomInvite);
    return () => {
      socket.off('chat:global', onGlobal);
      socket.off('chat:clan', onClan);
      socket.off('chat:dm', onDm);
      socket.off('friend:request', onFriendRequest);
      socket.off('friend:accepted', onFriendAccepted);
      socket.off('room:invite', onRoomInvite);
    };
  }, [me, open, tab, dmWith, bumpGlobal, bumpClan, bumpDm, bumpPending, setOpen, setTab, openDmFn]);

  return null;
}
